import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Key, ArrowRight, User } from 'lucide-react';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';

export function AdminImpersonate() {
  const [searchParams] = useSearchParams();
  const [pin, setPin] = useState('');
  const [targetUserId, setTargetUserId] = useState(searchParams.get('userId') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (searchParams.get('userId')) {
      handleDirectImpersonate(searchParams.get('userId')!);
    }
  }, []);

  const handleDirectImpersonate = async (userId: string) => {
    setLoading(true);
    setError('');
    try {
      // Find a brand for this user to land on
      const brandsRef = collection(db, 'brands');
      const q = query(brandsRef, where('ownerId', '==', userId), limit(1));
      const querySnapshot = await getDocs(q);
      
      let brandId = '';
      if (!querySnapshot.empty) {
        brandId = querySnapshot.docs[0].id;
      }

      localStorage.setItem('impersonatedUserId', userId);
      if (brandId) {
        localStorage.setItem('activeBrandId', brandId);
      }
      
      setSuccess(true);
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
    } catch (err: any) {
      setError('Failed to impersonate user.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (targetUserId) {
      await handleDirectImpersonate(targetUserId);
      return;
    }

    if (pin.length !== 6) {
      setError('PIN must be 6 digits or enter a User ID.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const pinDoc = await getDoc(doc(db, 'support_requests', pin));
      
      if (!pinDoc.exists()) {
        setError('Invalid or expired PIN.');
        setLoading(false);
        return;
      }

      const data = pinDoc.data();
      const expiresAt = new Date(data.expiresAt).getTime();
      
      if (Date.now() > expiresAt) {
        setError('This PIN has expired.');
        setLoading(false);
        return;
      }

      localStorage.setItem('impersonatedUserId', data.userId);
      localStorage.setItem('activeBrandId', data.brandId);
      
      setSuccess(true);
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1500);

    } catch (err: any) {
      setError('An error occurred while verifying the PIN.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6 relative overflow-hidden">
      <Helmet>
        <title>Admin Impersonate | PlataPost</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Abstract 3D Background Elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-600/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/20 rounded-full blur-[120px] pointer-events-none" />

      <div className="bg-gray-800/50 backdrop-blur-xl border border-gray-700 p-8 md:p-12 rounded-3xl shadow-2xl w-full max-w-md relative z-10">
        <div className="flex justify-center mb-8">
          <div className="bg-gray-900 p-4 rounded-2xl border border-gray-700 shadow-inner">
            <Shield className="w-12 h-12 text-indigo-400" />
          </div>
        </div>

        <h1 className="text-3xl font-black text-white text-center tracking-tight mb-2">Admin Access</h1>
        <p className="text-gray-400 text-center mb-8 text-sm">Directly impersonate any user or enter a 6-digit Support PIN.</p>

        {success ? (
          <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-4 rounded-xl text-center font-bold">
            Access Granted. Redirecting...
          </div>
        ) : (
          <form onSubmit={handleImpersonate} className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">User ID (Direct Access)</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input 
                  type="text" 
                  value={targetUserId}
                  onChange={(e) => setTargetUserId(e.target.value)}
                  placeholder="Paste User ID here..."
                  className="w-full bg-gray-900 border border-gray-700 text-white py-4 pl-12 pr-4 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                />
              </div>
            </div>

            <div className="relative flex items-center justify-center py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700"></div>
              </div>
              <span className="relative bg-gray-800 px-4 text-xs font-black text-gray-500 uppercase tracking-widest">or</span>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Support PIN</label>
              <div className="relative">
                <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input 
                  type="text" 
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full bg-gray-900 border border-gray-700 text-white text-2xl tracking-[0.5em] font-mono py-4 pl-12 pr-4 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-center"
                />
              </div>
            </div>

            {error && <p className="text-red-400 text-sm mt-2 font-medium">{error}</p>}

            <button 
              type="submit"
              disabled={loading || (!targetUserId && pin.length !== 6)}
              className="w-full bg-indigo-600 text-white py-4 rounded-xl font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? 'Verifying...' : 'Grant Access'}
              {!loading && <ArrowRight className="w-5 h-5" />}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
